import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useAuth } from '@/context/auth';

export type TipsNewsTag = 'New' | 'Tip' | 'Info' | 'BestPractice';
export type TipsNewsStatus = 'Draft' | 'Active';
export type TipsNewsDisplayStatus = 'Draft' | 'Active' | 'Scheduled' | 'Expired';

export interface TipsNewsFeedItem {
  id: string;
  tag: TipsNewsTag;
  titleEn: string;
  titleDe: string;
  bodyEn: string;
  bodyDe: string;
  ctaLabelEn: string;
  ctaLabelDe: string;
  ctaUrl: string;
  createdAt: string;
  isSeen: boolean;
}

export interface TipsNewsAdminItem extends TipsNewsFeedItem {
  tenantId: string | null;
  validFrom: string | null;
  validUntil: string | null;
  status: TipsNewsStatus;
  displayStatus: TipsNewsDisplayStatus;
  updatedAt: string | null;
}

export interface UpsertTipsNewsRequest {
  tag: TipsNewsTag;
  titleEn: string;
  titleDe: string;
  bodyEn: string;
  bodyDe: string;
  ctaLabelEn: string;
  ctaLabelDe: string;
  ctaUrl: string;
  validFrom: string | null;
  validUntil: string | null;
  status: TipsNewsStatus;
}

/**
 * EWM-799 DE/EN resolution (mirrors the maintenance-banner pattern): pick the variant for the active
 * UI language (de-* -> German, otherwise English), fall back to the other when blank.
 */
export const resolveLocalized = (en: string, de: string, locale: string): string => {
  const preferGerman = locale.toLowerCase().startsWith('de');
  const primary = preferGerman ? de : en;
  const secondary = preferGerman ? en : de;
  return primary || secondary || '';
};

const FEED_QUERY_KEY = ['tips-news', 'feed'] as const;
const TENANT_QUERY_KEY = ['tips-news', 'tenant-admin'] as const;
const FEED_URL = '/api/v1/tips-news';
const TENANT_URL = '/api/v1/tenant-admin/tips-news';
const TIPS_NEWS_POLL_MS = 120_000;

/**
 * Body for the two POSTs that take no payload. It exists only so axios sends
 * `Content-Type: application/json`, which BaseController's [Consumes] requires - see EWM-1003.
 * A `null` body gets `application/x-www-form-urlencoded` instead, which the API refuses with 415;
 * that is what broke Tips & News read state entirely.
 */
const EMPTY_JSON_BODY = {};

// --- user-facing feed -------------------------------------------------------------------------

export const useTipsNewsFeed = () => {
  const { auth } = useAuth();
  const authHeader = auth?.accessToken
    ? { Authorization: `Bearer ${auth.accessToken}` }
    : undefined;

  return useQuery<TipsNewsFeedItem[]>({
    queryKey: FEED_QUERY_KEY,
    enabled: Boolean(auth?.accessToken),
    queryFn: async () => {
      const { data } = await axios.get<TipsNewsFeedItem[]>(FEED_URL, { headers: authHeader });
      return data;
    },
    refetchInterval: TIPS_NEWS_POLL_MS,
    refetchIntervalInBackground: false,
  });
};

export const useMarkTipsNewsSeen = () => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, void>({
    mutationKey: ['tips-news-mark-seen'],
    mutationFn: async () => {
      // EWM-1003: the body must not be null. Every Estimator controller inherits
      // BaseController's [Consumes("application/json")], and for a null body axios sends
      // Content-Type: application/x-www-form-urlencoded - which that attribute refuses, so ASP.NET
      // answered 415 before the handler ever ran. The unread dot is cleared by this call alone, so
      // it never cleared: confirmed on TEST 2026-08-25, POST /api/v1/tips-news/mark-seen -> 415,
      // bell still blinking afterwards. An object makes axios send application/json.
      await axios.post(`${FEED_URL}/mark-seen`, EMPTY_JSON_BODY, {
        headers: auth?.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEY });
    },
  });
};

export const useDismissTipsNews = () => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationKey: ['tips-news-dismiss'],
    mutationFn: async (id) => {
      // EWM-1003: same 415 as mark-seen - "Got it" was equally broken, just less visible than a
      // bell that never stops blinking.
      await axios.post(`${FEED_URL}/${id}/dismiss`, EMPTY_JSON_BODY, {
        headers: auth?.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEY });
    },
  });
};

// --- tenant-admin management ------------------------------------------------------------------

export const useTenantTipsNews = (options?: { enabled?: boolean }) => {
  const { auth } = useAuth();
  const authHeader = auth?.accessToken
    ? { Authorization: `Bearer ${auth.accessToken}` }
    : undefined;

  return useQuery<TipsNewsAdminItem[]>({
    queryKey: TENANT_QUERY_KEY,
    enabled: Boolean(auth?.accessToken) && (options?.enabled ?? true),
    queryFn: async () => {
      const { data } = await axios.get<TipsNewsAdminItem[]>(TENANT_URL, { headers: authHeader });
      return data;
    },
  });
};

export const useUpsertTenantTipsNews = () => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<TipsNewsAdminItem, unknown, { id?: string; request: UpsertTipsNewsRequest }>({
    mutationKey: ['tenant-tips-news-upsert'],
    mutationFn: async ({ id, request }) => {
      const headers = auth?.accessToken
        ? { Authorization: `Bearer ${auth.accessToken}` }
        : undefined;
      const { data } = id
        ? await axios.put<TipsNewsAdminItem>(`${TENANT_URL}/${id}`, request, { headers })
        : await axios.post<TipsNewsAdminItem>(TENANT_URL, request, { headers });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEY });
      // A newly published/edited item may now be visible in the user's own feed.
      void queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEY });
    },
  });
};

export const useDeleteTenantTipsNews = () => {
  const { auth } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationKey: ['tenant-tips-news-delete'],
    mutationFn: async (id) => {
      await axios.delete(`${TENANT_URL}/${id}`, {
        headers: auth?.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANT_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEY });
    },
  });
};
